import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useSubscription } from '@apollo/client/react';
import { Badge, Box, Button, Card, Center, Flex, Grid, Heading, HStack, Icon, Separator, SimpleGrid, Spinner, Text, VStack, Wrap } from '@chakra-ui/react';
import { UserChip } from '@src/components/sco/Account/UserChip';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { AdvisorMiniProfile } from '@src/components/sco/Advisors/layout/AdvisorMiniProfile';
import { ManageEngagementPanel } from '@src/components/sco/Advisors/layout/ManageEngagementPanel';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { engagementToSeatData, seatAsksFromWhyText, seatStatusPalette } from '@src/components/sco/Advisors/utils/engagementMappers';
import { UserMatchesCardList } from '@src/components/sco/Matching/UserMatchesCardList';
import { USER_SEARCH_SUBSCRIPTION_FRAGMENT } from '@src/components/sco/Matching/userSearchFragment';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_ENGAGEMENT_STATUS_ACTIVE, ADVISOR_ENGAGEMENT_STATUS_INVITED } from '@src/constants/advisor-engagement-statuses';
import { ADVISOR_ENGAGEMENT_FRAGMENT } from '@src/gql/fragments/AdvisorEngagementFragment';
import { RESPOND_TO_SEAT_APPLICATION } from '@src/gql/mutations/RespondToSeatApplication';
import { GroupCommitment, ScoAdvisorEngagement, ScoUserSearch, UpsertUserSearchInput } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { IconType } from 'react-icons';
import { LuArrowLeft, LuCheck, LuCircleAlert, LuInbox, LuRadar, LuRefreshCw, LuSparkles, LuUsers } from 'react-icons/lu';
const ADVISOR_ENGAGEMENT_DETAIL_QUERY = gql`
  query SeatDetailSectionEngagement($advisorEngagementId: String!) {
    advisorEngagement(advisorEngagementId: $advisorEngagementId) {
      ...AdvisorEngagementFragment
      timeCommitmentNotes
      groupRequest {
        id
        groupRequestId
        publishStatusKey
        title
        userSearchId
        canUserEdit
      }
    }
  }
  ${ADVISOR_ENGAGEMENT_FRAGMENT}
`;
const SEAT_COMMITMENTS_QUERY = gql`
  query SeatDetailSectionCommitments($groupRequestId: String!, $page: Int, $pageSize: Int) {
    groupCommitments(groupRequestId: $groupRequestId, page: $page, pageSize: $pageSize) {
      items {
        id
        groupCommitmentId
        groupRequestId
        title
        description
        bodyAsMarkdown
        acceptedAt
        rejectedAt
        submittedAt
        createdAt
        committingUser {
          id
          userId
          slug
          fullName
          displayName
          pictureUrl
        }
        groupRequest {
          id
          canUserEdit
        }
      }
      pagination {
        itemCount
        hasNextPage
        page
        pageCount
      }
    }
  }
`;
const SEAT_USER_SEARCH_QUERY = gql`
  query SeatDetailSectionUserSearch($userSearchId: String!) {
    scoUserSearch(userSearchId: $userSearchId) {
      id
      userSearchId
      processingStatus
      processingDescription
      processingUpdatedAt
      updatedAt
      requiredSkills
      relevantSkills
      userMatches(page: 1, pageSize: 6, order: [["matchScore", "desc"]]) {
        id
        pagination {
          itemCount
        }
        items {
          id
          userMatchId
        }
      }
    }
  }
`;
const isProcessingStatus = (status?: string | null) => {
  if (!status || status === 'complete' || status === 'error') {
    return false;
  }
  return true;
};
const DetailCard = t0 => {
  const $ = _c(13);
  const {
    action,
    children,
    description,
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/55" textStyle="labelXs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = description ? <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{description}</Text> : null;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <VStack align="stretch" gap={1} minWidth={0}>{t1}{t2}</VStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== action || $[8] !== t3) {
    t4 = <HStack align="flex-start" gap={3} justify="space-between" wrap="wrap">{t3}{action}</HStack>;
    $[7] = action;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children || $[11] !== t4) {
    t5 = <Card.Root boxShadow="sm" variant="subtle"><Card.Body gap={4}>{t4}{children}</Card.Body></Card.Root>;
    $[10] = children;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
export const SeatSectionHeading = t0 => {
  const $ = _c(5);
  const {
    title
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Heading flexShrink={0} size="lg" whiteSpace="nowrap">{title}</Heading>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Separator flexGrow={1} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <HStack align="center" gap={4} width="full">{t1}{t2}</HStack>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const EmptyCallout = t0 => {
  const $ = _c(8);
  const {
    action,
    children,
    icon
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <Flex align="center" borderColor="colorPalette.border" borderRadius="full" borderStyle="dashed" borderWidth="1px" boxSize={12} color="colorPalette.fg/45" justify="center"><Icon boxSize={5}>{icon}</Icon></Flex>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm" maxWidth="md">{children}</Text>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== action || $[5] !== t1 || $[6] !== t2) {
    t3 = <Card.Root borderStyle="dashed" variant="outline"><Card.Body alignItems="center" gap={3} justifyContent="center" padding={8} textAlign="center">{t1}{t2}{action}</Card.Body></Card.Root>;
    $[4] = action;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export const SeatStatTile = t0 => {
  const $ = _c(13);
  const {
    colorPalette,
    hint,
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/55" textStyle="labelXs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <Heading size="2xl">{value}</Heading>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== hint) {
    t3 = hint ? <Text color="colorPalette.fg/60" fontSize="xs">{hint}</Text> : null;
    $[4] = hint;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <Card.Body gap={1} padding={4}>{t1}{t2}{t3}</Card.Body>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== colorPalette || $[11] !== t4) {
    t5 = <Card.Root colorPalette={colorPalette} size="sm" variant="subtle">{t4}</Card.Root>;
    $[10] = colorPalette;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
export const SeatDetailHero = t0 => {
  const $ = _c(69);
  const {
    category,
    icon: SeatIcon,
    palette,
    status,
    statusKey,
    why
  } = t0;
  let T0;
  let T1;
  let T2;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== SeatIcon || $[1] !== category || $[2] !== palette || $[3] !== status || $[4] !== statusKey || $[5] !== why) {
    const asks = seatAsksFromWhyText(why);
    const proseLines = why.split("\n").map(_temp).filter(_temp2);
    T2 = Card.Root;
    t7 = "colorPalette.card/40";
    t8 = "colorPalette.border/50";
    t9 = "1px";
    t10 = "md";
    t11 = "hidden";
    t12 = "subtle";
    T1 = Card.Body;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        base: 5,
        md: 6
      };
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    T0 = SimpleGrid;
    t1 = "start";
    const t13 = asks.length > 0 ? 2 : 1;
    if ($[22] !== t13) {
      t2 = {
        base: 1,
        md: t13
      };
      $[22] = t13;
      $[23] = t2;
    } else {
      t2 = $[23];
    }
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        base: 5,
        md: 8
      };
      $[24] = t3;
    } else {
      t3 = $[24];
    }
    let t14;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = {
        base: 4,
        md: 5
      };
      $[25] = t14;
    } else {
      t14 = $[25];
    }
    let t15;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = {
        base: 14,
        md: 20
      };
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    let t16;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = {
        base: 7,
        md: 8
      };
      $[27] = t16;
    } else {
      t16 = $[27];
    }
    let t17;
    if ($[28] !== SeatIcon) {
      t17 = <Flex align="center" bg="colorPalette.solid" borderRadius="l3" boxShadow="sm" boxSize={t15} color="colorPalette.contrast" flexShrink={0} justify="center"><Icon boxSize={t16}><SeatIcon /></Icon></Flex>;
      $[28] = SeatIcon;
      $[29] = t17;
    } else {
      t17 = $[29];
    }
    let t18;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Text color="colorPalette.fg/70" textStyle="labelXs">Board seat</Text>;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    let t19;
    if ($[31] !== statusKey) {
      t19 = seatStatusPalette(statusKey);
      $[31] = statusKey;
      $[32] = t19;
    } else {
      t19 = $[32];
    }
    let t20;
    if ($[33] !== status || $[34] !== t19) {
      t20 = <HStack gap={2} wrap="wrap">{t18}<Badge colorPalette={t19} variant="solid">{status}</Badge></HStack>;
      $[33] = status;
      $[34] = t19;
      $[35] = t20;
    } else {
      t20 = $[35];
    }
    let t21;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = {
        base: "2xl",
        md: "3xl"
      };
      $[36] = t21;
    } else {
      t21 = $[36];
    }
    let t22;
    if ($[37] !== category) {
      t22 = <Heading letterSpacing="-0.04em" size={t21}>{category}</Heading>;
      $[37] = category;
      $[38] = t22;
    } else {
      t22 = $[38];
    }
    let t23;
    if ($[39] !== t20 || $[40] !== t22) {
      t23 = <VStack align="stretch" flex={1} gap={2} minWidth={0} paddingTop={1}>{t20}{t22}</VStack>;
      $[39] = t20;
      $[40] = t22;
      $[41] = t23;
    } else {
      t23 = $[41];
    }
    let t24;
    if ($[42] !== palette || $[43] !== t17 || $[44] !== t23) {
      t24 = <HStack align="flex-start" colorPalette={palette} gap={t14}>{t17}{t23}</HStack>;
      $[42] = palette;
      $[43] = t17;
      $[44] = t23;
      $[45] = t24;
    } else {
      t24 = $[45];
    }
    const t25 = proseLines.map(_temp3);
    if ($[46] !== t24 || $[47] !== t25) {
      t4 = <VStack align="stretch" gap={4} minWidth={0}>{t24}{t25}</VStack>;
      $[46] = t24;
      $[47] = t25;
      $[48] = t4;
    } else {
      t4 = $[48];
    }
    t5 = asks.length > 0 ? <VStack align="stretch" bg="colorPalette.card/50" borderRadius="l2" boxShadow="sm" gap={3} padding={{
      base: 4,
      md: 5
    }}><Text color="colorPalette.fg/70" textStyle="labelXs">Asks</Text>{asks.map(_temp4)}</VStack> : null;
    $[0] = SeatIcon;
    $[1] = category;
    $[2] = palette;
    $[3] = status;
    $[4] = statusKey;
    $[5] = why;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = t1;
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    t1 = $[9];
    t10 = $[10];
    t11 = $[11];
    t12 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t13;
  if ($[49] !== T0 || $[50] !== t1 || $[51] !== t2 || $[52] !== t3 || $[53] !== t4 || $[54] !== t5) {
    t13 = <T0 alignItems={t1} columns={t2} gap={t3}>{t4}{t5}</T0>;
    $[49] = T0;
    $[50] = t1;
    $[51] = t2;
    $[52] = t3;
    $[53] = t4;
    $[54] = t5;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== T1 || $[57] !== t13 || $[58] !== t6) {
    t14 = <T1 padding={t6}>{t13}</T1>;
    $[56] = T1;
    $[57] = t13;
    $[58] = t6;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== T2 || $[61] !== t10 || $[62] !== t11 || $[63] !== t12 || $[64] !== t14 || $[65] !== t7 || $[66] !== t8 || $[67] !== t9) {
    t15 = <T2 bgColor={t7} borderColor={t8} borderWidth={t9} boxShadow={t10} overflow={t11} variant={t12}>{t14}</T2>;
    $[60] = T2;
    $[61] = t10;
    $[62] = t11;
    $[63] = t12;
    $[64] = t14;
    $[65] = t7;
    $[66] = t8;
    $[67] = t9;
    $[68] = t15;
  } else {
    t15 = $[68];
  }
  return t15;
};
export const SeatApplicationCard = t0 => {
  const $ = _c(38);
  const {
    busyId,
    canRespond,
    commitment,
    onAccept,
    onDecline
  } = t0;
  const accepted = !!commitment.acceptedAt;
  const declined = !!commitment.rejectedAt;
  const pending = !accepted && !declined;
  const railPalette = accepted ? "green" : declined ? "red" : pending ? "orange" : "gray";
  let t1;
  if ($[0] !== railPalette) {
    t1 = <Box bg="colorPalette.solid" colorPalette={railPalette} flexShrink={0} width="3px" />;
    $[0] = railPalette;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== commitment.committingUser) {
    t2 = <UserChip user={commitment.committingUser} />;
    $[2] = commitment.committingUser;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== accepted) {
    t3 = accepted ? <Badge colorPalette="green" variant="subtle">Accepted</Badge> : null;
    $[4] = accepted;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== declined) {
    t4 = declined ? <Badge colorPalette="red" variant="subtle">Declined</Badge> : null;
    $[6] = declined;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== pending) {
    t5 = pending ? <Badge colorPalette="orange" variant="subtle">Waiting</Badge> : null;
    $[8] = pending;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== commitment.committingUser) {
    t6 = commitment.committingUser?.slug ? <Button asChild={true} size="xs" variant="ghost"><NextLink href={`/members/${commitment.committingUser.slug}`}>View profile</NextLink></Button> : null;
    $[10] = commitment.committingUser;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <HStack gap={2} wrap="wrap">{t3}{t4}{t5}{t6}</HStack>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t7) {
    t8 = <HStack align="flex-start" gap={3} justify="space-between" wrap="wrap">{t2}{t7}</HStack>;
    $[17] = t2;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== commitment.description) {
    t9 = commitment.description ? <Text color="colorPalette.fg/80" fontFamily="longform" fontSize="sm">{commitment.description}</Text> : null;
    $[20] = commitment.description;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== commitment.bodyAsMarkdown) {
    t10 = commitment.bodyAsMarkdown ? <Text color="colorPalette.fg/80" fontFamily="longform" fontSize="sm">{commitment.bodyAsMarkdown}</Text> : null;
    $[22] = commitment.bodyAsMarkdown;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== busyId || $[25] !== canRespond || $[26] !== commitment.groupCommitmentId || $[27] !== onAccept || $[28] !== onDecline) {
    t11 = canRespond ? <HStack gap={2}><Button loading={busyId === commitment.groupCommitmentId} size="sm" variant="cta" onClick={onAccept}>Accept</Button><Button disabled={!!busyId} size="sm" variant="outline" onClick={onDecline}>Decline</Button></HStack> : null;
    $[24] = busyId;
    $[25] = canRespond;
    $[26] = commitment.groupCommitmentId;
    $[27] = onAccept;
    $[28] = onDecline;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t8 || $[33] !== t9) {
    t12 = <VStack align="stretch" flex={1} gap={3} minWidth={0} padding={4}>{t8}{t9}{t10}{t11}</VStack>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t8;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t1 || $[36] !== t12) {
    t13 = <Card.Root bgColor="colorPalette.card/50" overflow="hidden" size="sm" variant="elevated"><HStack align="stretch" gap={0}>{t1}{t12}</HStack></Card.Root>;
    $[35] = t1;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
};

/**
 * Suggested matches for an open seat. Kicks off / waits on UserSearch processing
 * (subscription + optional reprocess) instead of one-shot listing empty rows.
 */
const SeatSuggestedMatches = ({
  userSearchId
}: {
  userSearchId: string;
}) => {
  const searchQuery = useQuery(SEAT_USER_SEARCH_QUERY, {
    variables: {
      userSearchId
    },
    fetchPolicy: 'network-only'
  });
  const userSearch = searchQuery.data?.scoUserSearch as ScoUserSearch | null | undefined;
  const reprocessTriggered = useRef(false);
  useEffect(() => {
    reprocessTriggered.current = false;
  }, [userSearchId]);
  const [upsertUserSearch, reprocessMutation] = useMutation(gql`
    mutation SeatDetailReprocessUserSearch($input: UpsertUserSearchInput!) {
      upsertUserSearch(input: $input) {
        userSearch {
          id
          userSearchId
          processingStatus
          processingDescription
        }
      }
    }
  `);
  const searchSubscription = useSubscription(gql`
      subscription SeatDetailUserSearchUpdated($userSearchId: String!) {
        scoUserSearchUpdated(userSearchId: $userSearchId) {
          userSearch {
            id
            ${USER_SEARCH_SUBSCRIPTION_FRAGMENT}
          }
        }
      }
    `, {
    skip: !userSearchId,
    variables: {
      userSearchId
    }
  });

  // Live worker updates → refresh match list / processing state.
  useEffect(() => {
    const updated = searchSubscription.data?.scoUserSearchUpdated?.userSearch;
    if (updated?.userSearchId) {
      void searchQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to subscription payloads
  }, [searchSubscription.data]);

  // Auto-start matching when the linked search has never been processed.
  useEffect(() => {
    if (!userSearch?.userSearchId || reprocessTriggered.current) {
      return;
    }
    const status = userSearch.processingStatus;
    if (!status || status === 'pending') {
      reprocessTriggered.current = true;
      const input: UpsertUserSearchInput = {
        userSearchId: userSearch.userSearchId,
        reprocess: true
      };
      void upsertUserSearch({
        variables: {
          input
        }
      }).then(() => searchQuery.refetch());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trigger once per search id/status
  }, [userSearch?.userSearchId, userSearch?.processingStatus]);

  // Poll while processing in case websocket updates are missed.
  useEffect(() => {
    if (!isProcessingStatus(userSearch?.processingStatus)) {
      return;
    }
    const id = window.setInterval(() => {
      void searchQuery.refetch();
    }, 4000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch?.processingStatus, userSearchId]);
  const handleRefresh = async () => {
    reprocessTriggered.current = true;
    const input: UpsertUserSearchInput = {
      userSearchId,
      reprocess: true
    };
    await upsertUserSearch({
      variables: {
        input
      }
    });
    await searchQuery.refetch();
  };
  if (searchQuery.loading && !userSearch) {
    return <Center padding={6}>
        <Spinner />
      </Center>;
  }
  if (!userSearch) {
    return <EmptyCallout icon={<LuRadar />}>Matching is not available for this seat yet.</EmptyCallout>;
  }
  const matchCount = userSearch.userMatches?.pagination?.itemCount ?? 0;
  // null/pending = not started yet (we auto-reprocess); other non-complete statuses are in-flight.
  const processing = reprocessMutation.loading || !userSearch.processingStatus || userSearch.processingStatus === 'pending' || isProcessingStatus(userSearch.processingStatus);
  if (userSearch.processingStatus === 'error') {
    return <Card.Root colorPalette={'red'} variant={'subtle'}>
        <Card.Body gap={3}>
          <HStack align={'flex-start'} gap={3}>
            <Icon boxSize={5} color={'red.fg'} marginTop={0.5}>
              <LuCircleAlert />
            </Icon>
            <VStack align={'stretch'} gap={1}>
              <Text fontWeight={'semibold'}>Matching hit an error</Text>
              <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
                Try refreshing matches.
              </Text>
            </VStack>
          </HStack>
          <Button size={'sm'} variant={'outline'} width={'fit-content'} onClick={handleRefresh}>
            <LuRefreshCw />
            Refresh matches
          </Button>
        </Card.Body>
      </Card.Root>;
  }
  if (processing) {
    return <Card.Root colorPalette={'blue'} variant={'subtle'}>
        <Card.Body gap={3}>
          <HStack gap={2}>
            <Icon boxSize={5}>
              <LuRadar />
            </Icon>
            <Heading size={'sm'}>Matching advisors…</Heading>
          </HStack>
          <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
            {userSearch.processingDescription || 'We are scoring advisor candidates for this seat. Matches will appear automatically.'}
          </Text>
          <HStack gap={2}>
            <Spinner size={'sm'} />
            <Badge colorPalette={'blue'} variant={'subtle'}>
              {userSearch.processingStatus || 'starting'}
            </Badge>
          </HStack>
        </Card.Body>
      </Card.Root>;
  }

  // complete (or no status after process) — show list; emptyMessage covers zero results
  return <VStack align={'stretch'} gap={3} width={'full'}>
      {matchCount === 0 && <HStack justify={'flex-end'}>
          <Button loading={reprocessMutation.loading} size={'xs'} variant={'ghost'} onClick={handleRefresh}>
            <LuRefreshCw />
            Refresh matches
          </Button>
        </HStack>}
      <UserMatchesCardList hideTitle useWorkspaceUserMatchCard={true} variables={{
      userSearchId,
      pageSize: 6,
      order: [['matchScore', 'desc']]
    }} emptyMessage={<EmptyCallout icon={<LuSparkles />}>
            No algorithmic matches yet for this seat. Try refresh, or check back after advisors update their
            profiles.
          </EmptyCallout>} />
    </VStack>;
};
type SeatDetailSectionProps = {
  seatId?: string;
};
export const SeatDetailSection = ({
  seatId
}: SeatDetailSectionProps) => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const [applicationBusyId, setApplicationBusyId] = useState<string | null>(null);
  const [applicationError, setApplicationError] = useState<string | null>(null);
  const [showAddedBanner, setShowAddedBanner] = useState(false);
  const [respondToSeatApplication] = useMutation(RESPOND_TO_SEAT_APPLICATION);
  const engagementQuery = useQuery(ADVISOR_ENGAGEMENT_DETAIL_QUERY, {
    variables: {
      advisorEngagementId: seatId!
    },
    skip: !seatId,
    fetchPolicy: 'network-only'
  });
  const engagement = engagementQuery.data?.advisorEngagement as ScoAdvisorEngagement | null | undefined;
  const toolbarBreadcrumbs = useMemo(() => {
    if (!engagement?.category?.title) {
      return undefined;
    }
    return advisorsBreadcrumbsWithTrail({
      title: 'My Advisors',
      href: paths.myAdvisors
    }, {
      title: engagement.category.title,
      href: router.asPath
    });
  }, [engagement?.category?.title, paths.myAdvisors, router.asPath]);
  useAdvisorsToolbar({
    breadcrumbs: toolbarBreadcrumbs
  });
  useEffect(() => {
    if (router.query.added === '1') {
      setShowAddedBanner(true);
      const timeout = window.setTimeout(() => setShowAddedBanner(false), 5000);
      return () => window.clearTimeout(timeout);
    }
  }, [router.query.added]);
  const groupRequestId = engagement?.groupRequest?.groupRequestId;

  // Must run before any conditional return (rules-of-hooks). `skip` guards the network call.
  const commitmentsQuery = useQuery(SEAT_COMMITMENTS_QUERY, {
    skip: !groupRequestId,
    variables: {
      groupRequestId: groupRequestId!,
      page: 1,
      pageSize: 50
    },
    fetchPolicy: 'network-only'
  });
  const firstPageCommitments = (commitmentsQuery.data?.groupCommitments?.items ?? []).filter((item): item is GroupCommitment => !!item?.groupCommitmentId);
  const {
    items: loadedCommitments,
    loadMore: loadMoreCommitments,
    loadingMore: loadingMoreCommitments,
    hasNextPage: commitmentsHasNext
  } = useAdvisorsCollectionLoadMore({
    fetchMore: commitmentsQuery.fetchMore,
    field: 'groupCommitments',
    firstPageItems: firstPageCommitments,
    hasNextPage: commitmentsQuery.data?.groupCommitments?.pagination?.hasNextPage,
    resetKey: groupRequestId || ''
  });
  const handleApplication = async (groupCommitmentId: string, action: 'ACCEPT' | 'DECLINE') => {
    setApplicationError(null);
    setApplicationBusyId(groupCommitmentId);
    try {
      const result = await respondToSeatApplication({
        variables: {
          input: {
            groupCommitmentId,
            action
          }
        }
      });
      const next = result.data?.respondToSeatApplication?.advisorEngagement;
      await engagementQuery.refetch();
      await commitmentsQuery.refetch();
      if (action === 'ACCEPT') {
        const advisorUserId = next?.advisorUserId || next?.advisorUser?.userId;
        void router.push(paths.withQuery(paths.inbox, {
          advisorUserId: advisorUserId || undefined
        }));
      }
    } catch (err) {
      setApplicationError(err instanceof Error ? err.message : 'Could not update that application.');
    } finally {
      setApplicationBusyId(null);
    }
  };
  if (!seatId) {
    return <Center height={'full'} padding={8}>
        <Spinner size={'xl'} />
      </Center>;
  }
  if (engagementQuery.loading) {
    return <Center height={'full'} padding={8}>
        <Spinner size={'xl'} />
      </Center>;
  }
  if (!engagement) {
    return <Center height={'full'} padding={8}>
        <VStack gap={3}>
          <Heading size={'lg'}>Seat not found</Heading>
          <Button asChild variant={'outline'}>
            <NextLink href={paths.myAdvisors}>Back to My Advisors</NextLink>
          </Button>
        </VStack>
      </Center>;
  }
  const seat = engagementToSeatData(engagement);
  const commitments = loadedCommitments;
  const pendingApplications = commitments.filter(item_0 => !item_0.acceptedAt && !item_0.rejectedAt);
  const decidedApplications = commitments.filter(item_1 => !!item_1.acceptedAt || !!item_1.rejectedAt);
  const applications = [...pendingApplications, ...decidedApplications];
  const userSearchId = engagement.groupRequest?.userSearchId;
  const statusKey = engagement.statusKey;
  const hasAssignedAdvisor = !!engagement.advisorUserId || !!engagement.advisorUser || statusKey === ADVISOR_ENGAGEMENT_STATUS_INVITED || statusKey === ADVISOR_ENGAGEMENT_STATUS_ACTIVE;
  // Open recruiting seats keep candidates + algorithmic matches.
  const isOpenSeat = !hasAssignedAdvisor;
  const interestedCount = engagement.candidateCount ?? commitments.length;
  const canEditApplications = engagement.currentUserCanEdit !== false;
  const advisorHeading = statusKey === ADVISOR_ENGAGEMENT_STATUS_ACTIVE ? 'Your advisor' : 'Invited advisor';
  const managePanel = (showDetails: boolean) => <Card.Root boxShadow={'sm'} height={'full'} variant={'subtle'}>
      <Card.Body height={'full'}>
        <ManageEngagementPanel hideSeatLink engagement={engagement} profilePerspective={'advisor'} showDetails={showDetails} />
      </Card.Body>
    </Card.Root>;
  return <SectionScroll maxWidth={'6xl'}>
      <Button asChild padding={0} size={'sm'} variant={'ghost'} width={'fit-content'}>
        <NextLink href={paths.myAdvisors}>
          <LuArrowLeft /> Back to My Advisors
        </NextLink>
      </Button>

      {showAddedBanner ? <Card.Root colorPalette={'green'} variant={'subtle'}>
          <Card.Body>
            <HStack align={'flex-start'} gap={3}>
              <Icon boxSize={5} color={'green.fg'} marginTop={0.5}>
                <LuCheck />
              </Icon>
              <Text color={'colorPalette.fg/80'} fontFamily={'longform'} fontSize={'sm'}>
                Board seat added — wait for matches, or invite from the directory.
              </Text>
            </HStack>
          </Card.Body>
        </Card.Root> : null}

      <VStack align={'stretch'} gap={{
      base: 8,
      md: 10
    }}>
        <SeatDetailHero category={seat.category} icon={seat.icon} palette={seat.palette} status={seat.status} statusKey={statusKey} why={seat.why} />

        {hasAssignedAdvisor || isOpenSeat ? <VStack align={'stretch'} gap={5}>
            <SeatSectionHeading title={'Manage Board Seat'} />

            {hasAssignedAdvisor && engagement.advisorUser ? <Grid gap={5} templateColumns={{
          base: '1fr',
          lg: 'minmax(0, 1.45fr) minmax(260px, 0.9fr)'
        }}>
                <VStack align={'stretch'} gap={2}>
                  <Card.Root boxShadow={'sm'} variant={'subtle'}>
                    <Card.Body>
                      <Text color={'colorPalette.fg/55'} textStyle={'labelXs'}>
                        {advisorHeading}
                      </Text>
                      <AdvisorMiniProfile engagement={engagement} />
                    </Card.Body>
                  </Card.Root>
                </VStack>
                {managePanel(true)}
              </Grid> : hasAssignedAdvisor ?
        // Advisor id present but user payload missing — still surface name/email fallbacks.
        <Grid gap={5} templateColumns={{
          base: '1fr',
          lg: 'minmax(0, 1.45fr) minmax(260px, 0.9fr)'
        }}>
                <Card.Root boxShadow={'sm'} variant={'subtle'}>
                  <Card.Body gap={2}>
                    <Text color={'colorPalette.fg/55'} textStyle={'labelXs'}>
                      {advisorHeading}
                    </Text>
                    <Heading size={'lg'}>
                      {engagement.advisorName || engagement.advisorEmail || 'Advisor'}
                    </Heading>
                    {engagement.advisorEmail && engagement.advisorName ? <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                        {engagement.advisorEmail}
                      </Text> : null}
                  </Card.Body>
                </Card.Root>
                {managePanel(true)}
              </Grid> : null}

            {isOpenSeat ? <Grid gap={5} templateColumns={{
          base: '1fr',
          lg: 'minmax(0, 1.5fr) minmax(280px, 0.85fr)'
        }}>
                <VStack align={'stretch'} gap={5}>
                  <SimpleGrid columns={{
              base: 1,
              sm: 2
            }} gap={4}>
                    <SeatStatTile colorPalette={pendingApplications.length > 0 ? 'orange' : undefined} hint={pendingApplications.length > 0 ? 'Waiting for you' : 'No one is waiting'} label={'Applications'} value={pendingApplications.length} />
                    <SeatStatTile colorPalette={'blue'} hint={interestedCount === 1 ? 'Advisor interested' : 'Advisors interested'} label={'Interested'} value={interestedCount} />
                  </SimpleGrid>

                  <DetailCard label={'Looking for'}>
                    <SimpleGrid columns={{
                base: 1,
                md: 2
              }} gap={5}>
                      <VStack align={'stretch'} gap={2}>
                        <Text color={'colorPalette.fg/50'} textStyle={'label2xs'}>
                          Industries
                        </Text>
                        {seat.industries.length > 0 ? <Wrap gap={1.5}>
                            {seat.industries.map(industry => <Badge key={industry} colorPalette={seat.palette} textStyle={'label2xs'} variant={'subtle'}>
                                {industry}
                              </Badge>)}
                          </Wrap> : <Text color={'colorPalette.fg/45'} fontSize={'sm'}>
                            None listed
                          </Text>}
                      </VStack>
                      <VStack align={'stretch'} gap={2}>
                        <Text color={'colorPalette.fg/50'} textStyle={'label2xs'}>
                          Skills
                        </Text>
                        {seat.skills.length > 0 ? <Wrap gap={1.5}>
                            {seat.skills.map(skill => <SpecialtyTag key={skill} colorPalette={seat.palette} label={skill} />)}
                          </Wrap> : <Text color={'colorPalette.fg/45'} fontSize={'sm'}>
                            None listed
                          </Text>}
                      </VStack>
                    </SimpleGrid>
                  </DetailCard>

                  <DetailCard label={'Applications'} description={pendingApplications.length > 0 ? 'Accept or decline each application. Chat opens in Inbox after you accept — this is a board seat, not a paid session.' : undefined}>
                    {applicationError ? <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'}>
                        {applicationError}
                      </Text> : null}

                    {commitmentsQuery.loading ? <Center padding={6}>
                        <Spinner />
                      </Center> : null}

                    {!commitmentsQuery.loading && commitments.length === 0 ? <EmptyCallout icon={<LuInbox />}>
                        No applications yet. Wait for matches, or invite someone from Find advisors.
                      </EmptyCallout> : null}

                    {applications.length > 0 ? <VStack align={'stretch'} gap={3}>
                        {applications.map(commitment => {
                  const canRespond = isOpenSeat && canEditApplications && !commitment.acceptedAt && !commitment.rejectedAt;
                  return <SeatApplicationCard key={commitment.groupCommitmentId} busyId={applicationBusyId} canRespond={canRespond} commitment={commitment} onAccept={() => void handleApplication(commitment.groupCommitmentId!, 'ACCEPT')} onDecline={() => {
                    if (window.confirm('Decline this application?')) {
                      void handleApplication(commitment.groupCommitmentId!, 'DECLINE');
                    }
                  }} />;
                })}
                        <AdvisorsLoadMore hasNextPage={commitmentsHasNext} loading={loadingMoreCommitments} onLoadMore={loadMoreCommitments} />
                      </VStack> : null}
                  </DetailCard>
                </VStack>

                {managePanel(false)}
              </Grid> : null}
          </VStack> : null}

        {isOpenSeat ? <VStack align={'stretch'} gap={5}>
            <SeatSectionHeading title={'Suggested Matches'} />
            {userSearchId ? <SeatSuggestedMatches userSearchId={userSearchId} /> : <EmptyCallout icon={<LuUsers />} action={<Button asChild size={'sm'} variant={'outline'} width={'fit-content'}>
                    <NextLink href={paths.directory}>Find advisors to invite</NextLink>
                  </Button>}>
                Matching is off for this seat, or hasn&apos;t started yet. Invite someone from the directory
                while you wait.
              </EmptyCallout>}
          </VStack> : null}
      </VStack>
    </SectionScroll>;
};
function _temp(line) {
  return line.trim();
}
function _temp2(line_0) {
  return line_0 && !line_0.startsWith("\u2022");
}
function _temp3(line_1) {
  return <Text key={line_1} color="colorPalette.fg/80" fontFamily="longform" fontSize={{
    base: "md",
    md: "lg"
  }} lineHeight="tall">{line_1}</Text>;
}
function _temp4(ask) {
  return <HStack key={ask} align="center" gap={3}><Flex align="center" bg="green.solid/50" borderRadius="full" boxSize={6} color="colorPalette.fg" flexShrink={0} justify="center" marginTop={0.5}><Icon boxSize={3.5}><LuCheck /></Icon></Flex><Text color="colorPalette.fg" fontSize="sm" fontWeight="medium">{ask}</Text></HStack>;
}